import { Grid, Table as MTable, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useState } from 'react';
import { ageInYears, config, Expansion, ExpansionDetails, ExpansionSummary, formatDate, formatLocalDate, orange, organizationApi, personName, procedureSummary, truncateSpan } from '../../core';
import { LinkButton } from './presentation';

export interface FieldValueProps {
    label: string;
    value: any;
    row?: boolean;
    spacing?: number;
    containerStyle?: object;
    itemStyle?: object;
    valueStyle?:object;
    wrap?: boolean;
}


const space = '\u00A0';
export function FieldValue(props: FieldValueProps) {
    const { label, value, row, spacing, containerStyle, itemStyle, valueStyle, wrap } = props;
    const opt: any = row ? { direction: 'row', wrap: 'nowrap', spacing: spacing || 2, alignItems: 'flex-end' } :
        { direction: 'column', wrap: 'nowrap', spacing: spacing || 0, alignItems: 'flex-start' };

    const cStyle: any = { ...{ width: row ? '100%' : null }, ...containerStyle }
    const iStyle: any = { ...{ width: row ? '100%' : null}, ...itemStyle }
    const vStyle: any = {...{ whiteSpace: wrap ? undefined : 'nowrap', textAlign: row ? 'right' : undefined  }, ...valueStyle}
    return (
        <Grid container item {...opt} style={cStyle}>
            <Grid item style={iStyle}>
                <Typography
                    style={{ whiteSpace: 'nowrap' }}
                    variant='body2'
                    color='textSecondary'>
                    {label}
                </Typography>
            </Grid>
            <Grid item style={iStyle}>
                <Typography color='textPrimary' style={vStyle}>{value || space}</Typography>
            </Grid>
        </Grid>
    )
}

function VField({ label, value, style = undefined, truncate = NaN }) {
    return (
        <Grid container direction='column' style={style} >
            <Grid item>
                <Typography
                    style={{ whiteSpace: 'nowrap' }}
                    variant='body2'
                    color='textSecondary'>
                    {label}
                </Typography>
            </Grid>
            <Grid item >
                <Typography color='textPrimary' >
                    {isNaN(truncate) ? value : truncateSpan(value, truncate + 'px')}
                </Typography>
            </Grid>
        </Grid>
    )
}
type PatientInfoProps = any & {
    cornerComponent?: any,
    patient: any,
    mrn: string,
    accountNumber: string
}
export function PatientInfo({ patient, mrn, accountNumber, cornerComponent }: PatientInfoProps) {
    const { lastName, firstName, gender, birthDate } = patient || {};
    return (
        <>
            <Grid container
                style={{ flexWrap: 'nowrap', width: '100%' }}
                direction='row'
                spacing={2}
                alignItems='center'>
                <FieldValue label='Patient' value={lastName + ', ' + firstName} />
                <Grid item style={{flexGrow: 1}} />
              
                {
                    cornerComponent 
                }
            </Grid>
            <Grid container
                style={{ flexWrap: 'nowrap', width: '100%' }}
                direction='row'
                spacing={2}
                alignItems='center'>

                <FieldValue label='Account' value={accountNumber} />
                <VField label='MRN' value={mrn} truncate={67} />
                <FieldValue label='Date of Birth' value={birthDate ? formatLocalDate(birthDate) : ''} />
                <FieldValue label='Age' containerStyle={{ width: 50 }} value={birthDate ? ageInYears(birthDate) : ''} />
                <FieldValue label='Gender' value={(gender || '').charAt(0)} />
            </Grid>
        </>
    )
}

export function InsuranceInfo({ insurances } : any) {
    return insurances?.length > 0 && (
        <Grid container
            item
            style={{ flexWrap: 'nowrap', width: '100%' }}
            direction='row'
            spacing={2}
            alignItems='center'>
            <FieldValue label='Primary Insurance' value={insurances[0]?.companyName} />
            {
                insurances[1] &&
                <FieldValue label='Secondary Insurance' value={insurances[1]?.companyName} />
            }
        </Grid>
    )
}

export function SideBar({ bundle }) {
    const { details, documents, dfts } = bundle
    const { accountNumber, worksheetId } = details;
    const {patient, mrn } = details?.codingQueue;
    const list = documents?.result.worksheetComponents;

    const {visitId} = documents?.result;


    const locationCode = organizationApi.getState().selection.location.code;
    const departmentCode = organizationApi.getState().selection.department.code;
    

    const organizationCode = organizationApi.getState().selection.organization.code;
    const url = `${
       config.codingAppUrl
    }/visit?vis=${visitId}&wid=${worksheetId}&loc=${locationCode}&dep=${departmentCode}&org=${organizationCode}`;
  

    const cornerComponent = (
        <Grid item style={{ flexWrap: 'nowrap' }}>
          <a
            href={url}
            style={{ whiteSpace: 'nowrap', color: orange }}
            rel="noopener noreferrer"
            target="_blank"
          >
            {' '}
            Open Worksheet
          </a>
        </Grid>
      );

    return (
        <Grid container direction='column' style={{overflow: 'hidden !important', flexWrap: 'nowrap' }}>
            <PatientInfo accountNumber={accountNumber} patient={patient} mrn={mrn} cornerComponent={cornerComponent}/>
            <br />
            <InsuranceInfo {...details?.codingQueue} />
            {list.map((t, i) => (
                <>
                <WorksheetSummary {...t} />
                {!!dfts?.length && <Dft dft={dfts[i % dfts.length]} />}
                </>
            ))}
        </Grid>
    )
}

const sideBarWidth = 550;

function WorksheetSummary({ transitionLogs, worksheet }: any) {
    const { state, firstDateInCoding, departmentCode } = worksheet || {};
    const w: any = { width: '100%', flexWrap: 'nowrap'  };
  
    return (
      <Grid container direction="column" style={w}>
        <Grid
          item
          container
          direction="row"
          style={{ ...w, ...{ paddingTop: 2 } }}
        >
          <FieldValue label="State" value={state} />
          <FieldValue label="Department" value={departmentCode} />
          <FieldValue
            label="First Date in Coding"
            value={formatDate(firstDateInCoding)}
          />
        </Grid>
  
         <WorksheetElements worksheet={worksheet} />
         <WorksheetTransitions transitionLogs={transitionLogs} /> 
      </Grid>
    );
  }

  function WorksheetTransitions({ transitionLogs }) {
    const logs = transitionLogs || [];
    const width = Math.floor(sideBarWidth * 0.24);
    const cellStyle = { paddingTop: 0, paddingBottom: 0, fontSize: 9 };
    const w: any = { width: '100%', flexWrap: 'nowrap' };
    const widthPx = width - 36 + 'px';
    const activityWidth = Math.floor(sideBarWidth * 0.2);
    const [expanded, setExpanded] = useState(true);
  
    return (
      <Expansion expanded={expanded} onClick={() => setExpanded(!expanded)}>
        <ExpansionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="body2" color="textSecondary">
            Transition History
          </Typography>
        </ExpansionSummary>
        <ExpansionDetails>
          <Grid item container direction="column" style={w}>
            <TableContainer
              component={(a: any) =>
                (
                  <Grid
                    item
                    {...a}
                    style={{
                      width: sideBarWidth - 40,
                      overflowX: 'hidden',
                      fontSize: 9,
                      overflowY: 'hidden',
                      border: '1px solid #ccc',
                      flexWrap: 'nowrap',
                    }}
                  />
                ) as any
              }
            >
              <MTable size="small" style={{ tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    <TableCell style={{ width: activityWidth }}>Person</TableCell>
                    <TableCell style={{ width: activityWidth }}>
                      Activity
                    </TableCell>
                    <TableCell align="left">From</TableCell>
                    <TableCell align="left">To</TableCell>
                    <TableCell align="center">Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map((a: any) => (
                    <TableRow key={a.workflowActivity}>
                      <TableCell
                        component={'th' as any}
                        scope="row"
                        style={cellStyle}
                      >
                        {a.createdBy && personName(a.createdBy)}
                      </TableCell>
                      <TableCell
                        component={'th' as any}
                        scope="row"
                        style={cellStyle}
                      >
                        {truncateSpan(a.workflowActivity, activityWidth + 'px')}
                      </TableCell>
                      <TableCell align="left" style={cellStyle}>
                        {truncateSpan(a.fromState, widthPx)}
                      </TableCell>
                      <TableCell align="left" style={cellStyle}>
                        {truncateSpan(a.toState, widthPx)}
                      </TableCell>
                      <TableCell align="right" style={cellStyle}>
                        {formatDate(a.createDate)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </MTable>
            </TableContainer>
          </Grid>
        </ExpansionDetails>
      </Expansion>
    );
  }

  
  type DftProps = {
    dft: any;
  };
  function Dft({ dft }: DftProps) {

    const { billingString } = dft;
  
    const w: any = { width: 510, flexWrap: 'nowrap' };
    const [expanded, setExpanded] = useState(true);
  
    return (
      <Expansion expanded={expanded}>
        <ExpansionSummary
          expandIcon={<ExpandMoreIcon />}
          onClick={() => setExpanded(!expanded)}
        >
          <Typography variant="body2" color="textSecondary">
            Billing DFT
          </Typography>
        </ExpansionSummary>
        <ExpansionDetails>
          <Grid item container direction="column" style={w}>
            <Grid
              item
              style={{
                paddingLeft: 6,
                overflowWrap: 'break-word',
                whiteSpace: 'pre-wrap',
              }}
            >
              {billingString?.replaceAll('\r', '\n')}
            </Grid>
          </Grid>
        </ExpansionDetails>
      </Expansion>
    );
  }
  
  function icdLink(a: any) {
    return `${a.diagnosisCode} - ${a.dxDescription}`;
  }
  
  function cptLink(a: any) {
    return `${procedureSummary(a, true, true)} - ${a.description}`;
  }

  export function WorksheetElements({ worksheet }: any) {
    let { diagnoses, procedures, workflowOwners } = worksheet;
    const [expanded, setExpanded] = useState(true);
  
    const title = 'Worksheet Summary';
  
    diagnoses = (diagnoses || []).filter((a) => a.active);
    procedures = (procedures || []).filter((a) => a.active);
  
    const onIcd = (a: any) => {};
    const onCpt = (a: any) => {};
  
    return (
      <Expansion expanded={expanded} onClick={() => setExpanded(!expanded)}>
        <ExpansionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="body2" color="textSecondary">
            {title}
          </Typography>
        </ExpansionSummary>
        <ExpansionDetails>
          <Grid
            container
            direction="column"
            alignItems="flex-start"
            style={{
              width: 515,
              maxHeight: '72vh',
              overflowX: 'hidden',
              overflowY: 'hidden',
              flexWrap: 'wrap',
            }}
          >
            {workflowOwners?.length > 0 && (
              <Grid
                item
                container
                direction="row"
                style={{ width: '100%', paddingLeft: 6 }}
              >
                <Grid item>
                  <Typography variant="body2" style={{ paddingBottom: 6 }}>
                    Workflow Owners
                  </Typography>
  
                  {workflowOwners.map((v, i) => (
                    <Grid
                      item
                      container
                      direction="row"
                      style={{ width: '100%', paddingLeft: 6 }}
                    >
                      <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">
                          {personName(v)}
                        </Typography>
                      </Grid>
  
                      <Grid item xs={6}>
                        <Typography variant="body2">{v.workflow} </Typography>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            )}
  
            {!!diagnoses?.length && (
              <ElementList
                list={diagnoses}
                label="Diagnoses"
                linkFn={icdLink}
                onClick={onIcd}
              />
            )}
  
            {!!procedures?.length && (
              <ElementList
                list={procedures}
                label="Procedures"
                linkFn={cptLink}
                onClick={onCpt}
              />
            )}
          </Grid>
        </ExpansionDetails>
      </Expansion>
    );
  }

  function ElementList(props: any) {
    const { label, list, linkFn, onClick, width } = props;
  
    return (
      <Expansion
        defaultExpanded={true}
        style={{ width: 540, paddingLeft: '6px' }}
        expanded={true}
      >
        <ExpansionSummary>
          <Typography variant="body2">{label}</Typography>
        </ExpansionSummary>
        <ExpansionDetails>
          <Grid
            style={{ width: '90%', paddingLeft: '6px' }}
            container
            direction="column"
          >
            {list.map((v: any, i: number) => (
              <ElementLink
                key={i}
                el={v}
                linkFn={linkFn}
                onClick={onClick}
                width={520}
              />
            ))}
          </Grid>
        </ExpansionDetails>
      </Expansion>
    );
  }

  function ElementLink(props: any) {
    const { linkFn, onClick, el, width } = props;
    const link = linkFn(el);

    return (
      <Grid style={{ width: '99%', }} container item direction="row">
        <Grid item>
          <LinkButton
            label={link}
            color={orange}
            style={{whiteSpace: 'pre-wrap'}}
            onClick={() => onClick(el)}
            width={width || '99%'}
          />
        </Grid>
      </Grid>
    );
  }
  
